import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Input, Modal, Table, Form, Button } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

// Import action creators directly from the slice
import {
  setTerms,
  setTermsLoading,
  setTermsError,
} from "../../redux/termsSlice";

// Assuming these are your actual imports from your project structure
import routes from "../../api/routes";
import { callApi } from "../../api/apiCaller";
import Loader from "../../components/loader/loader";
import { GreenNotify, RedNotify } from "../../helper/helper";
import { getBodyPlainText } from "../../utils/getPlainTextFromHtml";

// ✅ Corrected template with a placeholder for content injection
const defaultHtmlTemplate = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Terms of Use – FITBIZZ</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            margin: 0;
            padding: 0;
            background-color: #f4f4f4;
            color: #333;
        }
        .container {
            width: 80%;
            margin: 0 auto;
            padding: 20px;
            background-color: #fff;
        }
        h1, h2 {
            font-weight: bold;
            color: #333;
        }
        p {
            font-size: 16px;
            margin-bottom: 1em;
        }
        ul {
            margin-bottom: 1em;
            padding-left: 20px;
        }
        ul li {
            margin-bottom: 0.5em;
        }
        a {
            color: #007bff;
            text-decoration: none;
        }
        a:hover {
            text-decoration: underline;
        }
        em {
            font-style: italic;
        }
    </style>
</head>
<body>
    <div class="container">
        <!--USER_CONTENT_HERE-->
    </div>
</body>
</html>`;

const TermsAndConditions = () => {
  const dispatch = useDispatch();
  const { terms, isLoading } = useSelector((state) => state.terms);

  // Local state for UI controls
  const [searchText, setSearchText] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isEditingMode, setIsEditingMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  // API call logic wrapped in useCallback
  const fetchTerms = useCallback(() => {
    dispatch(setTermsLoading(true));
    callApi(
      "GET",
      routes.getTerms,
      null,
      () => {},
      (res) => {
        const singleTerm = res;
        const normalized = Array.isArray(singleTerm)
          ? singleTerm
          : singleTerm
          ? [singleTerm]
          : [];
        dispatch(setTerms(normalized));
      },
      (err) => {
        const errorMessage = err.message || "Could not fetch terms.";
        dispatch(setTermsError(errorMessage));
        RedNotify(errorMessage);
      }
    );
  }, [dispatch]);

  useEffect(() => {
    fetchTerms();
  }, [fetchTerms]);

  const handleCreate = () => {
    setIsEditingMode(false);
    setEditingId(null);
    form.setFieldsValue({ content: "" });
    setIsModalVisible(true);
  };
  const handleSubmit = () => {
    form
      .validateFields()
      .then((values) => {
        const { content } = values;
        let finalContent = content;

        // If the content is simple text/HTML (not a full document),
        // inject it into our default template.
        if (!content.trim().toLowerCase().includes("<html")) {
          // ✅ Correctly replace the placeholder with the editor content
          finalContent = defaultHtmlTemplate.replace(
            "<!--USER_CONTENT_HERE-->",
            content
          );
        }

        const method = isEditingMode ? "PUT" : "POST";
        const url = isEditingMode
          ? `${routes.updateTerms}/${editingId}`
          : routes.createTerms;

        const body = { ...values, content: finalContent };

        dispatch(setTermsLoading(true));
        // ✅ Re-enabled the API call
        callApi(
          method,
          url,
          body,
          () => {},
          () => {
            GreenNotify(
              `Term ${isEditingMode ? "updated" : "created"} successfully`
            );
            fetchTerms();
            setIsModalVisible(false);
          },
          (err) => {
            const errorMessage = err.message || "Failed to submit term";
            dispatch(setTermsError(errorMessage));
            RedNotify(errorMessage);
          }
        );
      })
      .catch((validationErr) => {
        console.error("Validation Error:", validationErr);
      });
  };

  const filteredData = terms
    ?.filter((term) =>
      getBodyPlainText(term.content)
        .toLowerCase()
        .includes(searchText.toLowerCase())
    )
    .map((term) => ({
      ...term,
      key: term._id,
      createdAt: new Date(term.createdAt).toLocaleString(),
    }));

  const columns = [
    {
      title: "Version",
      dataIndex: "version",
      align: "center",
      width: 220,
    },
    {
      title: "Content",
      dataIndex: "content",
      align: "left",
      render: (html) => <div>{getBodyPlainText(html, 150)}</div>,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      align: "center",
      width: 220,
    },
  ];

  return (
    <div className="admin-terms-main-container">
      <Loader loading={isLoading} />
      <div style={{ margin: "1rem 1.5rem" }}>
        <h2>Terms & Conditions</h2>
        <div style={{ display: "flex", gap: "1rem", marginBlock: "1rem" }}>
          <Input
            placeholder="Search by content"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: "300px" }}
          />
          <Button type="primary" onClick={handleCreate}>
            + Add Term
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="_id"
          scroll={{ x: "max-content" }}
          onRow={(record) => ({
            onClick: () => {
              setSelectedRecord(record);
              setIsDetailModalVisible(true);
            },
            style: { cursor: "pointer" },
          })}
        />
      </div>
      {/* Create/Edit Modal */}
      <Modal
        title={isEditingMode ? "Edit Term" : "Create Term"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSubmit}
        okText={isEditingMode ? "Update" : "Create"}
        width={800}
        destroyOnClose
      >
        <Form form={form} layout="vertical" initialValues={{ content: "" }}>
          <Form.Item
            label="Content"
            name="content"
            rules={[
              {
                validator: async (_, value) => {
                  const plain = getBodyPlainText(value, null);
                  if (
                    !value ||
                    value === "<p><br></p>" ||
                    plain.trim() === ""
                  ) {
                    return Promise.reject(
                      new Error("Content is required and cannot be empty.")
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ReactQuill
              theme="snow"
              style={{ height: "250px", marginBottom: "50px" }}
              placeholder="Enter simple text or paste a full HTML document..."
            />
          </Form.Item>
        </Form>
      </Modal>
      {/* Detail View Modal */}
      <Modal
        title="Term Details"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedRecord && (
          <div>
            <p>
              <strong>Created At:</strong> {selectedRecord.createdAt}
            </p>
            <p
              style={{
                fontWeight: "bold",
                marginTop: "16px",
                marginBottom: "8px",
              }}
            >
              Content:
            </p>
            <div
              style={{
                border: "1px solid #d9d9d9",
                padding: "16px",
                maxHeight: "60vh",
                backgroundColor: "#fff", // White background
                borderRadius: "2px",
              }}
            >
              <iframe
                style={{
                  width: "100%",
                  height: "55vh",
                  border: "none",
                }}
                title="Term Content"
                srcDoc={selectedRecord.content}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TermsAndConditions;
